import {
  Box,
  Button,
  Center,
  Collapse,
  Divider,
  Flex,
  Input,
  Text,
  Textarea,
} from '@chakra-ui/react'
import { ProcedureCategory, ProcedureName } from '@hb/shared'
import { useEffect, useState } from 'react'
import { useField, useForm } from 'react-final-form'

// ─── Datetime Helpers ─────────────────────────────────────────────────────────

const tsToDatetimeLocal = (ts: number | undefined): string => {
  if (!ts) return ''
  const d = new Date(ts)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const fromDatetimeLocal = (s: string): number | undefined => {
  if (!s) return undefined
  return new Date(s).getTime()
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface ProcedureMeta {
  label: string
  category: ProcedureCategory
  color: string
  scheme: string
  description: string
}

// ─── Procedure Catalog ────────────────────────────────────────────────────────

const PROCEDURE_META: Record<ProcedureName, ProcedureMeta> = {
  [ProcedureName.AMNIOTOMY]: {
    label: 'Amniotomy (AROM)',
    category: ProcedureCategory.INTERVENTION,
    color: '#3182CE',
    scheme: 'blue',
    description: 'Artificial rupture of membranes',
  },
  [ProcedureName.FOLEY_BULB]: {
    label: 'Foley Bulb',
    category: ProcedureCategory.INTERVENTION,
    color: '#805AD5',
    scheme: 'purple',
    description: 'Mechanical cervical ripening',
  },
  [ProcedureName.EPIDURAL_PLACEMENT]: {
    label: 'Epidural Placement',
    category: ProcedureCategory.ANESTHESIA,
    color: '#38A169',
    scheme: 'green',
    description: 'Regional anesthesia placement',
  },
  [ProcedureName.SUTURING]: {
    label: 'Perineal Repair',
    category: ProcedureCategory.REPAIR,
    color: '#DD6B20',
    scheme: 'orange',
    description: 'Perineal laceration repair',
  },
  [ProcedureName.FSE_IUPC]: {
    label: 'FSE / IUPC',
    category: ProcedureCategory.MONITORING,
    color: '#D69E2E',
    scheme: 'yellow',
    description: 'Internal fetal/uterine monitoring',
  },
}

// ─── Section Label ────────────────────────────────────────────────────────────

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize="lg" fontFamily="hero-new" opacity={0.8} fontWeight={400}>
    {children}
  </Text>
)

// ─── Consent Gate ─────────────────────────────────────────────────────────────

/**
 * Step 1 — must confirm consent before accessing procedure details.
 * Surfaced as a prominent banner so the clinician can never miss it.
 */
const ConsentGate = ({ onConsentGiven }: { onConsentGiven: () => void }) => {
  const { input } = useField<boolean>('consentObtained', { subscription: { value: true } })

  return (
    <Flex
      w="100%"
      flexFlow="column"
      gap={3}
      bg={input.value ? 'green.50' : 'white'}
      _dark={{ bg: input.value ? 'rgba(72,187,120,0.1)' : '#2a2a2a' }}
      border="2px solid"
      borderColor={input.value ? 'green.300' : 'gray.300'}
      borderRadius={14}
      p={4}
      transition="all 0.2s">
      <Flex align="center" gap={3} justify="space-between">
        <Flex flexFlow="column" gap={0.5}>
          <Text
            fontSize="md"
            fontWeight={700}
            fontFamily="hero-new"
            color={input.value ? 'green.700' : 'gray.700'}
            _dark={{ color: input.value ? 'green.300' : 'gray.200' }}>
            {input.value ? '✓ Informed Consent Obtained' : 'Consent Status'}
          </Text>
          <Text
            fontSize="xs"
            fontFamily="hero-new"
            color={input.value ? 'green.600' : 'gray.500'}
            _dark={{ color: input.value ? 'green.400' : 'gray.400' }}>
            {input.value
              ? 'Patient verbalized understanding of risks, benefits, and alternatives.'
              : 'Confirm informed consent before documenting procedure details.'}
          </Text>
        </Flex>
        {input.value ? (
          <Button
            size="sm"
            variant="ghost"
            colorScheme="green"
            fontFamily="hero-new"
            onClick={() => input.onChange(false)}>
            Revoke
          </Button>
        ) : (
          <Button
            size="sm"
            colorScheme="teal"
            borderRadius={8}
            fontFamily="hero-new"
            fontWeight={600}
            onClick={() => {
              input.onChange(true)
              onConsentGiven()
            }}>
            Confirm Consent
          </Button>
        )}
      </Flex>
      {!input.value ? (
        <Flex
          bg="orange.50"
          _dark={{ bg: 'rgba(237,137,54,0.12)' }}
          borderRadius={8}
          px={3}
          py={2}
          align="center"
          gap={2}>
          <Text fontSize="xs" fontFamily="hero-new" color="orange.700">
            ⚠ Consent must be documented before procedure start time for audit trail compliance.
          </Text>
        </Flex>
      ) : null}
    </Flex>
  )
}

// ─── Procedure Selector ───────────────────────────────────────────────────────

const ProcedureSelector = () => {
  const { change } = useForm()
  const { input: nameInput } = useField<string>('procedureName', { subscription: { value: true } })
  const [showCustom, setShowCustom] = useState(
    !!nameInput.value && !Object.keys(PROCEDURE_META).includes(nameInput.value),
  )

  const selectProcedure = (id: string, meta: ProcedureMeta | null) => {
    change('procedureName', id)
    change('category', meta?.category ?? null)
    setShowCustom(false)
  }

  // All options: 5 named procedures + "Other" = 6 → 3 rows of 2
  const allOptions = [
    ...Object.entries(PROCEDURE_META).map(([id, meta]) => ({ id, meta })),
    { id: '__other__', meta: null },
  ]
  const rows: (typeof allOptions)[] = []
  for (let i = 0; i < allOptions.length; i += 2) {
    rows.push(allOptions.slice(i, i + 2))
  }

  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <SectionLabel>PROCEDURE</SectionLabel>
      <Flex flexFlow="column" gap={2} w="100%">
        {rows.map((row, rowIdx) => (
          <Flex key={rowIdx} gap={2} w="100%">
            {row.map(opt =>
              opt.id === '__other__' ? (
                <Button
                  key="__other__"
                  flex={1}
                  minH="52px"
                  variant={showCustom ? 'solid' : 'outline'}
                  colorScheme={showCustom ? 'blue' : 'gray'}
                  borderRadius={10}
                  fontFamily="hero-new"
                  fontWeight={showCustom ? 600 : 400}
                  fontSize="md"
                  onClick={() => {
                    if (!showCustom) change('procedureName', '')
                    setShowCustom(v => !v)
                  }}>
                  Other…
                </Button>
              ) : (
                <Button
                  key={opt.id}
                  flex={1}
                  h="auto"
                  minH="52px"
                  py={2}
                  variant={nameInput.value === opt.id && !showCustom ? 'solid' : 'outline'}
                  colorScheme={
                    nameInput.value === opt.id && !showCustom
                      ? (opt.meta?.scheme ?? 'gray')
                      : 'gray'
                  }
                  borderRadius={10}
                  onClick={() => selectProcedure(opt.id, opt.meta)}>
                  <Flex flexFlow="column" align="center" gap={0.5}>
                    <Text
                      fontSize="md"
                      fontWeight={nameInput.value === opt.id && !showCustom ? 600 : 400}
                      fontFamily="hero-new">
                      {opt.meta?.label}
                    </Text>
                    <Text
                      fontSize="sm"
                      opacity={0.7}
                      fontFamily="hero-new"
                      whiteSpace="normal"
                      textAlign="center">
                      {opt.meta?.description}
                    </Text>
                  </Flex>
                </Button>
              ),
            )}
          </Flex>
        ))}
      </Flex>
      {showCustom ? (
        <Input
          value={nameInput.value}
          onChange={e => change('procedureName', e.target.value)}
          placeholder="Enter procedure name…"
          fontFamily="hero-new"
          fontSize="md"
          bg="white"
          _dark={{ bg: '#2a2a2a' }}
          borderRadius={10}
          h="44px"
          autoFocus
        />
      ) : null}
    </Flex>
  )
}

// ─── Amniotomy Detail Form ────────────────────────────────────────────────────

type FluidColor = 'clear' | 'meconium_thin' | 'meconium_thick' | 'bloody'
type FluidOdor = 'none' | 'foul'

const FLUID_COLORS: {
  id: FluidColor
  label: string
  sub: string
  color: string
  scheme: string
}[] = [
  { id: 'clear', label: 'Clear', sub: 'normal', color: '#63B3ED', scheme: 'blue' },
  {
    id: 'meconium_thin',
    label: 'Thin Mec',
    sub: 'light staining',
    color: '#D69E2E',
    scheme: 'yellow',
  },
  {
    id: 'meconium_thick',
    label: 'Thick Mec',
    sub: 'particulate',
    color: '#C05621',
    scheme: 'orange',
  },
  { id: 'bloody', label: 'Bloody', sub: 'hemorrhagic', color: '#E53E3E', scheme: 'red' },
]

const AmniotomyDetails = () => {
  const { change } = useForm()
  const { input: detailsInput } = useField<Record<string, string>>('details', {
    subscription: { value: true },
  })

  const details = detailsInput.value ?? {}
  const fluidColor = (details.fluidColor ?? '') as FluidColor | ''
  const fluidOdor = (details.fluidOdor ?? 'none') as FluidOdor
  const postFhr = details.postFhr ?? ''

  const setDetail = (key: string, val: string) => change('details', { ...details, [key]: val })

  const hasMeconium = fluidColor === 'meconium_thin' || fluidColor === 'meconium_thick'

  return (
    <Flex flexFlow="column" w="100%" gap={4}>
      {/* Fluid Color */}
      <Flex flexFlow="column" w="100%" gap={2}>
        <SectionLabel>FLUID COLOR</SectionLabel>
        <Flex gap={2} wrap="wrap" w="100%">
          {FLUID_COLORS.map(opt => {
            const isActive = fluidColor === opt.id
            return (
              <Button
                key={opt.id}
                flex={1}
                minW="80px"
                h="56px"
                variant={isActive ? 'solid' : 'outline'}
                colorScheme={isActive ? opt.scheme : 'gray'}
                borderRadius={10}
                onClick={() => setDetail('fluidColor', opt.id)}>
                <Flex flexFlow="column" align="center" gap={0}>
                  <Text fontSize="sm" fontWeight={isActive ? 600 : 400} fontFamily="hero-new">
                    {opt.label}
                  </Text>
                  <Text fontSize="xs" opacity={0.7} fontFamily="hero-new">
                    {opt.sub}
                  </Text>
                </Flex>
              </Button>
            )
          })}
        </Flex>
        {hasMeconium ? (
          <Flex
            bg="orange.50"
            _dark={{ bg: 'rgba(237,137,54,0.15)' }}
            border="1px solid"
            borderColor="orange.300"
            borderRadius={10}
            px={3}
            py={2}>
            <Text fontSize="xs" fontFamily="hero-new" color="orange.700" fontWeight={600}>
              ⚠ Meconium present — notify provider, prepare neonatal resuscitation team per
              protocol.
            </Text>
          </Flex>
        ) : null}
      </Flex>

      {/* Odor */}
      <Flex flexFlow="column" w="100%" gap={2}>
        <SectionLabel>FLUID ODOR</SectionLabel>
        <Flex gap={2} w="100%">
          {(['none', 'foul'] as FluidOdor[]).map(odor => {
            const isActive = fluidOdor === odor
            return (
              <Button
                key={odor}
                flex={1}
                h="44px"
                variant={isActive ? 'solid' : 'outline'}
                colorScheme={isActive ? (odor === 'foul' ? 'red' : 'teal') : 'gray'}
                borderRadius={10}
                fontFamily="hero-new"
                fontWeight={isActive ? 600 : 400}
                fontSize="sm"
                onClick={() => setDetail('fluidOdor', odor)}>
                {odor === 'none' ? 'No Odor' : 'Foul Odor'}
              </Button>
            )
          })}
        </Flex>
      </Flex>

      {/* Immediate post-AROM FHR */}
      <Flex flexFlow="column" w="100%" gap={2}>
        <Flex align="center" gap={2}>
          <SectionLabel>POST-AROM FHR</SectionLabel>
          <Text fontSize="xs" fontFamily="hero-new" color="blue.400" fontWeight={600}>
            Required
          </Text>
        </Flex>
        <Text fontSize="xs" fontFamily="hero-new" color="gray.400" mt={-1}>
          Document fetal heart rate immediately following membrane rupture.
        </Text>
        <Flex align="center" gap={3}>
          <Input
            type="number"
            value={postFhr}
            onChange={e => setDetail('postFhr', e.target.value)}
            placeholder="140"
            fontFamily="hero-new"
            fontSize="xl"
            fontWeight={600}
            bg="white"
            _dark={{ bg: '#2a2a2a' }}
            borderRadius={10}
            h="48px"
            w="120px"
            textAlign="center"
          />
          <Text fontFamily="hero-new" color="gray.500" fontSize="md">
            bpm
          </Text>
          {postFhr && Number(postFhr) < 110 ? (
            <Text fontSize="xs" fontFamily="hero-new" color="red.500" fontWeight={600}>
              ⚠ Below normal — document response
            </Text>
          ) : postFhr && Number(postFhr) > 160 ? (
            <Text fontSize="xs" fontFamily="hero-new" color="orange.500" fontWeight={600}>
              ⚠ Tachycardia — monitor closely
            </Text>
          ) : null}
        </Flex>
      </Flex>
    </Flex>
  )
}

// ─── Foley Bulb Detail Form ───────────────────────────────────────────────────

const FoleyBulbDetails = () => {
  const { change } = useForm()
  const { input: detailsInput } = useField<Record<string, string>>('details', {
    subscription: { value: true },
  })

  const details = detailsInput.value ?? {}
  const cc = details.balloonCc ?? '30'
  const setDetail = (key: string, val: string) => change('details', { ...details, [key]: val })

  const CC_OPTIONS = ['30', '40', '50', '60']

  return (
    <Flex flexFlow="column" w="100%" gap={4}>
      <Flex flexFlow="column" w="100%" gap={2}>
        <SectionLabel>BALLOON INFLATION VOLUME</SectionLabel>
        <Flex gap={2} w="100%">
          {CC_OPTIONS.map(opt => {
            const isActive = cc === opt
            return (
              <Button
                key={opt}
                flex={1}
                h="52px"
                variant={isActive ? 'solid' : 'outline'}
                colorScheme={isActive ? 'purple' : 'gray'}
                borderRadius={10}
                fontFamily="hero-new"
                fontWeight={isActive ? 600 : 400}>
                <Flex
                  flexFlow="column"
                  align="center"
                  gap={0}
                  onClick={() => setDetail('balloonCc', opt)}>
                  <Text fontSize="lg">{opt}</Text>
                  <Text fontSize="xs" opacity={0.7}>
                    cc
                  </Text>
                </Flex>
              </Button>
            )
          })}
        </Flex>
      </Flex>

      <Flex flexFlow="column" w="100%" gap={2}>
        <SectionLabel>CATHETER SIZE</SectionLabel>
        <Flex gap={2} wrap="wrap" w="100%">
          {['16 Fr', '18 Fr', '20 Fr'].map(size => {
            const isActive = details.catheterSize === size
            return (
              <Button
                key={size}
                flex={1}
                h="44px"
                variant={isActive ? 'solid' : 'outline'}
                colorScheme={isActive ? 'purple' : 'gray'}
                borderRadius={10}
                fontFamily="hero-new"
                fontWeight={isActive ? 600 : 400}
                fontSize="sm"
                onClick={() => setDetail('catheterSize', size)}>
                {size}
              </Button>
            )
          })}
        </Flex>
      </Flex>

      <Flex flexFlow="column" w="100%" gap={2}>
        <SectionLabel>STERILE WATER USED</SectionLabel>
        <Flex align="center" gap={3}>
          <Input
            type="number"
            value={details.sterileWaterCc ?? ''}
            onChange={e => setDetail('sterileWaterCc', e.target.value)}
            placeholder={cc}
            fontFamily="hero-new"
            fontSize="xl"
            fontWeight={600}
            bg="white"
            _dark={{ bg: '#2a2a2a' }}
            borderRadius={10}
            h="48px"
            w="120px"
            textAlign="center"
          />
          <Text fontFamily="hero-new" color="gray.500">
            cc sterile water
          </Text>
        </Flex>
      </Flex>
    </Flex>
  )
}

// ─── Epidural Detail Form ─────────────────────────────────────────────────────

const EpiduralDetails = () => {
  const { change } = useForm()
  const { input: detailsInput } = useField<Record<string, string>>('details', {
    subscription: { value: true },
  })

  const details = detailsInput.value ?? {}
  const setDetail = (key: string, val: string) => change('details', { ...details, [key]: val })

  const LEVELS = ['L2-L3', 'L3-L4', 'L4-L5']
  const TEST_DOSE_RESPONSES = [
    { id: 'negative', label: 'Negative', sub: 'No response', scheme: 'green' },
    { id: 'intravascular', label: 'Intravascular', sub: 'Positive', scheme: 'red' },
    { id: 'intrathecal', label: 'Intrathecal', sub: 'Positive', scheme: 'orange' },
  ]

  return (
    <Flex flexFlow="column" w="100%" gap={4}>
      <Flex flexFlow="column" w="100%" gap={2}>
        <SectionLabel>INSERTION LEVEL</SectionLabel>
        <Flex gap={2} w="100%">
          {LEVELS.map(level => {
            const isActive = details.level === level
            return (
              <Button
                key={level}
                flex={1}
                h="44px"
                variant={isActive ? 'solid' : 'outline'}
                colorScheme={isActive ? 'green' : 'gray'}
                borderRadius={10}
                fontFamily="hero-new"
                fontWeight={isActive ? 600 : 400}
                onClick={() => setDetail('level', level)}>
                {level}
              </Button>
            )
          })}
        </Flex>
      </Flex>

      <Flex flexFlow="column" w="100%" gap={2}>
        <SectionLabel>TEST DOSE RESPONSE</SectionLabel>
        <Flex gap={2} wrap="wrap" w="100%">
          {TEST_DOSE_RESPONSES.map(opt => {
            const isActive = details.testDoseResponse === opt.id
            return (
              <Button
                key={opt.id}
                flex={1}
                h="56px"
                variant={isActive ? 'solid' : 'outline'}
                colorScheme={isActive ? opt.scheme : 'gray'}
                borderRadius={10}
                onClick={() => setDetail('testDoseResponse', opt.id)}>
                <Flex flexFlow="column" align="center" gap={0}>
                  <Text fontSize="sm" fontWeight={isActive ? 600 : 400} fontFamily="hero-new">
                    {opt.label}
                  </Text>
                  <Text fontSize="xs" opacity={0.7} fontFamily="hero-new">
                    {opt.sub}
                  </Text>
                </Flex>
              </Button>
            )
          })}
        </Flex>
        {details.testDoseResponse && details.testDoseResponse !== 'negative' ? (
          <Flex
            bg="red.50"
            _dark={{ bg: 'rgba(229,62,62,0.12)' }}
            border="1px solid"
            borderColor="red.300"
            borderRadius={10}
            px={3}
            py={2}>
            <Text fontSize="xs" fontFamily="hero-new" color="red.700" fontWeight={600}>
              ⚠ Positive test dose — do not inject full epidural dose. Notify anesthesiologist
              immediately.
            </Text>
          </Flex>
        ) : null}
      </Flex>

      <Flex flexFlow="column" w="100%" gap={2}>
        <SectionLabel>ANESTHETIC AGENT</SectionLabel>
        <Input
          value={details.anestheticAgent ?? ''}
          onChange={e => setDetail('anestheticAgent', e.target.value)}
          placeholder="e.g. Bupivacaine 0.25% + Fentanyl 2 mcg/mL"
          fontFamily="hero-new"
          fontSize="sm"
          bg="white"
          _dark={{ bg: '#2a2a2a' }}
          borderRadius={10}
          h="44px"
        />
      </Flex>
    </Flex>
  )
}

// ─── Perineal Repair — Suture Map ─────────────────────────────────────────────

type TearSite = 'anterior' | 'posterior_1' | 'posterior_2' | 'left_lateral' | 'right_lateral'
type TearDegree = '1st' | '2nd' | '3rd' | '4th'

interface TearLocation {
  id: TearSite
  label: string
  cx: number
  cy: number
}

const TEAR_LOCATIONS: TearLocation[] = [
  { id: 'anterior', label: 'Anterior', cx: 100, cy: 30 },
  { id: 'posterior_1', label: 'Post (prox)', cx: 100, cy: 130 },
  { id: 'posterior_2', label: 'Post (dist)', cx: 100, cy: 165 },
  { id: 'left_lateral', label: 'Left Lateral', cx: 48, cy: 100 },
  { id: 'right_lateral', label: 'Right Lateral', cx: 152, cy: 100 },
]

const TEAR_DEGREES: { id: TearDegree; label: string; sub: string; scheme: string }[] = [
  { id: '1st', label: '1st', sub: 'skin only', scheme: 'green' },
  { id: '2nd', label: '2nd', sub: 'muscle involved', scheme: 'yellow' },
  { id: '3rd', label: '3rd', sub: 'external sphincter', scheme: 'orange' },
  { id: '4th', label: '4th', sub: 'rectal mucosa', scheme: 'red' },
]

const SUTURE_TYPES = ['3-0 Vicryl', '2-0 Vicryl', '3-0 Chromic', '2-0 Chromic', '4-0 Monocryl']

const SutureMap = () => {
  const { change } = useForm()
  const { input: detailsInput } = useField<Record<string, string>>('details', {
    subscription: { value: true },
  })

  const details = detailsInput.value ?? {}
  const selectedSite = details.tearSite as TearSite | undefined
  const setDetail = (key: string, val: string) => change('details', { ...details, [key]: val })

  return (
    <Flex flexFlow="column" w="100%" gap={4}>
      {/* Anatomical tap map */}
      <Flex flexFlow="column" w="100%" gap={2}>
        <Flex align="center" gap={2}>
          <SectionLabel>LACERATION SITE</SectionLabel>
          <Text fontSize="xs" fontFamily="hero-new" color="gray.400">
            Tap to select location
          </Text>
        </Flex>
        <Flex justify="center" w="100%">
          <Box w="200px" h="200px" position="relative">
            <svg width="200" height="200" viewBox="0 0 200 200">
              {/* Perineum outline — simplified anatomical representation */}
              {/* Vaginal opening */}
              <ellipse
                cx="100"
                cy="80"
                rx="35"
                ry="50"
                fill="rgba(237,137,54,0.08)"
                stroke="#CBD5E0"
                strokeWidth="1.5"
              />
              {/* Vaginal introitus label area */}
              <text
                x="100"
                y="84"
                textAnchor="middle"
                fontSize="9"
                fontFamily="hero-new, sans-serif"
                fill="#A0AEC0">
                vaginal
              </text>
              <text
                x="100"
                y="95"
                textAnchor="middle"
                fontSize="9"
                fontFamily="hero-new, sans-serif"
                fill="#A0AEC0">
                opening
              </text>
              {/* Perineal body */}
              <ellipse
                cx="100"
                cy="145"
                rx="20"
                ry="12"
                fill="rgba(99,179,237,0.1)"
                stroke="#BEE3F8"
                strokeWidth="1"
              />
              {/* Touch targets */}
              {TEAR_LOCATIONS.map(loc => {
                const isSelected = selectedSite === loc.id
                return (
                  <g
                    key={loc.id}
                    onClick={() => setDetail('tearSite', loc.id)}
                    style={{ cursor: 'pointer' }}>
                    <circle
                      cx={loc.cx}
                      cy={loc.cy}
                      r={isSelected ? 14 : 10}
                      fill={isSelected ? '#E53E3E' : '#EDF2F7'}
                      stroke={isSelected ? '#C53030' : '#CBD5E0'}
                      strokeWidth={isSelected ? 2.5 : 1.5}
                      opacity={isSelected ? 1 : 0.85}
                    />
                    <text
                      x={loc.cx}
                      y={loc.cy + 4}
                      textAnchor="middle"
                      fontSize="8"
                      fontFamily="hero-new, sans-serif"
                      fontWeight={isSelected ? '700' : '400'}
                      fill={isSelected ? 'white' : '#718096'}>
                      {loc.label.split(' ')[0]}
                    </text>
                  </g>
                )
              })}
            </svg>
            {selectedSite ? (
              <Box
                position="absolute"
                bottom={-2}
                left="50%"
                transform="translateX(-50%)"
                bg="red.500"
                color="white"
                borderRadius="full"
                px={2}
                py={0.5}>
                <Text fontSize="10px" fontFamily="hero-new" whiteSpace="nowrap" fontWeight={600}>
                  {TEAR_LOCATIONS.find(l => l.id === selectedSite)?.label}
                </Text>
              </Box>
            ) : null}
          </Box>
        </Flex>
      </Flex>

      {/* Degree selector */}
      <Flex flexFlow="column" w="100%" gap={2}>
        <SectionLabel>LACERATION DEGREE</SectionLabel>
        <Flex gap={2} w="100%">
          {TEAR_DEGREES.map(deg => {
            const isActive = details.tearDegree === deg.id
            return (
              <Button
                key={deg.id}
                flex={1}
                h="56px"
                variant={isActive ? 'solid' : 'outline'}
                colorScheme={isActive ? deg.scheme : 'gray'}
                borderRadius={10}
                onClick={() => setDetail('tearDegree', deg.id)}>
                <Flex flexFlow="column" align="center" gap={0}>
                  <Text fontSize="md" fontWeight={isActive ? 700 : 400} fontFamily="hero-new">
                    {deg.label}
                  </Text>
                  <Text fontSize="9px" opacity={0.75} fontFamily="hero-new">
                    {deg.sub}
                  </Text>
                </Flex>
              </Button>
            )
          })}
        </Flex>
        {details.tearDegree === '3rd' || details.tearDegree === '4th' ? (
          <Flex
            bg="red.50"
            _dark={{ bg: 'rgba(229,62,62,0.12)' }}
            border="1px solid"
            borderColor="red.300"
            borderRadius={10}
            px={3}
            py={2}>
            <Text fontSize="xs" fontFamily="hero-new" color="red.700" fontWeight={600}>
              ⚠ {details.tearDegree} degree laceration — OB/surgeon consultation recommended.
            </Text>
          </Flex>
        ) : null}
      </Flex>

      {/* Suture type */}
      <Flex flexFlow="column" w="100%" gap={2}>
        <SectionLabel>SUTURE MATERIAL</SectionLabel>
        <Flex gap={2} wrap="wrap" w="100%">
          {SUTURE_TYPES.map(suture => {
            const isActive = details.sutureType === suture
            return (
              <Button
                key={suture}
                h="40px"
                px={3}
                variant={isActive ? 'solid' : 'outline'}
                colorScheme={isActive ? 'orange' : 'gray'}
                borderRadius={8}
                fontFamily="hero-new"
                fontWeight={isActive ? 600 : 400}
                fontSize="sm"
                onClick={() => setDetail('sutureType', suture)}>
                {suture}
              </Button>
            )
          })}
        </Flex>
      </Flex>
    </Flex>
  )
}

// ─── FSE/IUPC Detail Form ─────────────────────────────────────────────────────

const FseIupcDetails = () => {
  const { change } = useForm()
  const { input: detailsInput } = useField<Record<string, string>>('details', {
    subscription: { value: true },
  })

  const details = detailsInput.value ?? {}
  const setDetail = (key: string, val: string) => change('details', { ...details, [key]: val })

  const INDICATIONS = [
    'Inability to trace externally',
    'Suspected non-reassuring FHR',
    'Obesity/positioning',
    'Inadequate contraction assessment',
  ]

  return (
    <Flex flexFlow="column" w="100%" gap={4}>
      <Flex flexFlow="column" w="100%" gap={2}>
        <SectionLabel>DEVICE TYPE</SectionLabel>
        <Flex gap={2} w="100%">
          {['FSE', 'IUPC', 'Both'].map(device => {
            const isActive = details.deviceType === device
            return (
              <Button
                key={device}
                flex={1}
                h="52px"
                variant={isActive ? 'solid' : 'outline'}
                colorScheme={isActive ? 'yellow' : 'gray'}
                borderRadius={10}
                fontFamily="hero-new"
                fontWeight={isActive ? 600 : 400}
                onClick={() => setDetail('deviceType', device)}>
                <Flex flexFlow="column" align="center" gap={0}>
                  <Text fontSize="md">{device}</Text>
                  <Text fontSize="9px" opacity={0.7}>
                    {device === 'FSE' ? 'Fetal Scalp' : device === 'IUPC' ? 'Intrauterine' : 'Both'}
                  </Text>
                </Flex>
              </Button>
            )
          })}
        </Flex>
      </Flex>

      <Flex flexFlow="column" w="100%" gap={2}>
        <SectionLabel>INDICATION</SectionLabel>
        <Flex gap={2} wrap="wrap" w="100%">
          {INDICATIONS.map(indication => {
            const isActive = details.indication === indication
            return (
              <Button
                key={indication}
                h="auto"
                minH="40px"
                py={2}
                px={3}
                variant={isActive ? 'solid' : 'outline'}
                colorScheme={isActive ? 'yellow' : 'gray'}
                borderRadius={8}
                fontFamily="hero-new"
                fontWeight={isActive ? 600 : 400}
                fontSize="sm"
                whiteSpace="normal"
                textAlign="center"
                onClick={() => setDetail('indication', indication)}>
                {indication}
              </Button>
            )
          })}
        </Flex>
      </Flex>
    </Flex>
  )
}

// ─── Generic Details ──────────────────────────────────────────────────────────

const GenericDetails = () => {
  const { change } = useForm()
  const { input: detailsInput } = useField<Record<string, string>>('details', {
    subscription: { value: true },
  })
  const details = detailsInput.value ?? {}

  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <SectionLabel>PROCEDURE DETAILS</SectionLabel>
      <Textarea
        value={details.notes ?? ''}
        onChange={e => change('details', { ...details, notes: e.target.value })}
        placeholder="Document procedure specifics, findings, patient response…"
        fontFamily="hero-new"
        fontSize="md"
        bg="white"
        _dark={{ bg: '#2a2a2a' }}
        borderRadius={10}
        minH="120px"
        resize="vertical"
      />
    </Flex>
  )
}

// ─── Procedure-Specific Detail Switch ────────────────────────────────────────

const ProcedureDetails = ({ procedureName }: { procedureName: ProcedureName }) => {
  switch (procedureName) {
    case ProcedureName.AMNIOTOMY:
      return <AmniotomyDetails />
    case ProcedureName.FOLEY_BULB:
      return <FoleyBulbDetails />
    case ProcedureName.EPIDURAL_PLACEMENT:
      return <EpiduralDetails />
    case ProcedureName.SUTURING:
      return <SutureMap />
    case ProcedureName.FSE_IUPC:
      return <FseIupcDetails />
    default:
      return procedureName ? <GenericDetails /> : null
  }
}

// ─── Outcome Toggle ───────────────────────────────────────────────────────────

const OutcomeToggle = () => {
  const { input } = useField<boolean>('isSuccessful', { subscription: { value: true } })
  const isSet = typeof input.value === 'boolean'

  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <SectionLabel>OUTCOME</SectionLabel>
      <Flex gap={2} w="100%">
        {([true, false] as const).map(val => {
          const isActive = isSet && input.value === val
          return (
            <Button
              key={String(val)}
              flex={1}
              h="48px"
              variant={isActive ? 'solid' : 'outline'}
              colorScheme={isActive ? (val ? 'green' : 'red') : 'gray'}
              borderRadius={10}
              fontFamily="hero-new"
              fontWeight={isActive ? 600 : 400}
              onClick={() => input.onChange(val)}>
              {val ? 'Successful' : 'Unsuccessful / Attempted'}
            </Button>
          )
        })}
      </Flex>
    </Flex>
  )
}

// ─── Timing Inputs ────────────────────────────────────────────────────────────

const ProcedureTimingInput = () => {
  const { input: startInput } = useField<number>('startTime', { subscription: { value: true } })
  const { input: endInput } = useField<number | undefined>('endTime', {
    subscription: { value: true },
  })

  // Default startTime to now on mount if not yet set
  useEffect(() => {
    if (!startInput.value) startInput.onChange(Date.now())
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Flex flexFlow="column" w="100%" gap={3}>
      <SectionLabel>TIMING</SectionLabel>
      <Flex flexFlow="column" gap={2}>
        <Text fontSize="sm" fontFamily="hero-new" color="gray.500">
          Start Time
        </Text>
        <Input
          type="datetime-local"
          value={tsToDatetimeLocal(startInput.value)}
          onChange={e => {
            const ts = fromDatetimeLocal(e.target.value)
            if (ts !== undefined) startInput.onChange(ts)
          }}
          fontFamily="hero-new"
          fontSize="md"
          bg="white"
          _dark={{ bg: '#2a2a2a' }}
          borderRadius={10}
          h="48px"
        />
      </Flex>
      <Flex flexFlow="column" gap={2}>
        <Text fontSize="sm" fontFamily="hero-new" color="gray.500">
          End Time (optional)
        </Text>
        <Input
          type="datetime-local"
          value={tsToDatetimeLocal(endInput.value)}
          onChange={e => {
            endInput.onChange(fromDatetimeLocal(e.target.value))
          }}
          fontFamily="hero-new"
          fontSize="md"
          bg="white"
          _dark={{ bg: '#2a2a2a' }}
          borderRadius={10}
          h="48px"
        />
      </Flex>
    </Flex>
  )
}

// ─── Provider Input ───────────────────────────────────────────────────────────

const ProviderInput = () => {
  const { input: providerInput } = useField<string>('providerId', { subscription: { value: true } })
  const { input: assistantInput } = useField<string>('assistantId', {
    subscription: { value: true },
  })

  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <SectionLabel>PROVIDERS</SectionLabel>
      <Flex flexFlow="column" gap={2} w="100%">
        <Input
          value={providerInput.value ?? ''}
          onChange={e => providerInput.onChange(e.target.value)}
          placeholder="Primary provider name / ID"
          fontFamily="hero-new"
          fontSize="md"
          bg="white"
          _dark={{ bg: '#2a2a2a' }}
          borderRadius={10}
          h="44px"
        />
        <Input
          value={assistantInput.value ?? ''}
          onChange={e => assistantInput.onChange(e.target.value)}
          placeholder="Assisting provider (optional)"
          fontFamily="hero-new"
          fontSize="sm"
          bg="white"
          _dark={{ bg: '#2a2a2a' }}
          borderRadius={10}
          h="40px"
        />
      </Flex>
    </Flex>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const ProcedureEntry = () => {
  const { input: nameInput } = useField<string>('procedureName', { subscription: { value: true } })
  const { input: consentInput } = useField<boolean>('consentObtained', {
    subscription: { value: true },
  })

  const [consentJustGiven, setConsentJustGiven] = useState(false)

  const procedureName = nameInput.value ?? ''
  const hasConsent = consentInput.value ?? false

  // The detail form is only revealed after consent is confirmed
  const showDetails = hasConsent && !!procedureName

  return (
    <Center pb={8} w="100%">
      <Flex flexFlow="column" w="100%" maxW="560px" py={4} px={2} gap={5} align="center">
        {/* Step 1 — Consent */}
        <ConsentGate onConsentGiven={() => setConsentJustGiven(true)} />

        <Divider />

        {/* Step 2 — Procedure Name */}
        <ProcedureSelector />

        <Divider />

        {/* Steps 3–N — Procedure-specific details, unlocked after consent */}
        <Collapse unmountOnExit in={showDetails} style={{ width: '100%' }}>
          <Flex flexFlow="column" w="100%" gap={5}>
            {consentJustGiven || hasConsent ? (
              <Flex
                bg="green.50"
                _dark={{ bg: 'rgba(72,187,120,0.08)' }}
                border="1px solid"
                borderColor="green.200"
                borderRadius={10}
                px={3}
                py={2}
                align="center"
                gap={2}>
                <Text fontSize="xs" fontFamily="hero-new" color="green.700">
                  ✓ Consent confirmed before start time — audit trail requirement met.
                </Text>
              </Flex>
            ) : null}
            <ProcedureTimingInput />
            <Divider />
            <ProcedureDetails procedureName={procedureName as ProcedureName} />
            <Divider />
            <OutcomeToggle />
            <Divider />
            <ProviderInput />
          </Flex>
        </Collapse>

        {/* Nudge when procedure selected but no consent yet */}
        {!hasConsent && procedureName ? (
          <Flex
            w="100%"
            border="1px dashed"
            borderColor="gray.300"
            _dark={{ borderColor: 'gray.600' }}
            borderRadius={12}
            px={4}
            py={3}
            align="center"
            gap={2}>
            <Text fontSize="sm" fontFamily="hero-new" color="gray.500">
              Confirm consent above to unlock procedure details.
            </Text>
          </Flex>
        ) : null}
      </Flex>
    </Center>
  )
}
